/**
 * Links from outside the phone app open INSIDE it.
 *
 * An email, a WhatsApp message or an SMS carries an ordinary `https://<company>.peopleorbit.in/...`
 * link (a training invite `/j/:token`, a course `/learning/...`, a live session
 * `/training/live/...`). On a phone with the app installed, Android hands such a link to the app
 * (the verified App Links set up in docs/mobile/APP_LINKS.md) instead of the browser, and
 * Capacitor reports it as `appUrlOpen`. This turns it into a React Router navigation, so the person
 * lands on the same screen the link names, with the same route guards as in a browser.
 *
 * Only PeopleOrbit's own hosts count (`peopleorbit.in` and any subdomain of it). The allowed root
 * comes from `APP_DOMAIN`, the same value the login screen and the subdomain handoff use, so a
 * link to any other site is ignored and can never steer the app somewhere else.
 *
 * One more link is not a web address: `peopleorbit://sso?code=&next=`, the way a Zoho/Google sign-in that
 * ran in the system browser comes back to the app (lib/nativeSso.ts). It closes the in-app browser
 * and goes to `/session-handoff`, which redeems the code with the app's PKCE verifier. A code is
 * followed once: it is single-use on the server, and a second navigation would only show an error.
 * With `&kind=signup` it is a different code for a different reason — a sign-up still missing its
 * company details and mobile number, not yet a session — and goes to `/signup` instead, which
 * redeems it for the verified identity to continue the form with, natively, rather than in the tab.
 *
 * On the web the hook does nothing: a browser follows its own links.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { listenForRestoredContact } from './contactPicker.js';
import { parseAppReturnUrl, peekAppVerifier } from './nativeSso.js';
import { APP_DOMAIN } from './subdomain.js';

/**
 * The in-app route (`path + search + hash`) for an opened link, or `null` when it should be ignored:
 * not https, not a PeopleOrbit host, or not a URL at all.
 *
 * `rootDomain` defaults to `APP_DOMAIN`. A host matches when it IS the root or ends with `.root`;
 * `evilpeopleorbit.in` and `peopleorbit.in.example.com` do not.
 */
export function routeForOpenedUrl(url: string, rootDomain: string = APP_DOMAIN): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  const root = rootDomain.trim().toLowerCase();
  if (!root) return null;
  const host = parsed.hostname.toLowerCase();
  if (host !== root && !host.endsWith(`.${root}`)) return null;
  // `//x` would read as a protocol-relative address; keep it a path of this app.
  const path = parsed.pathname.replace(/^\/{2,}/, '/') || '/';
  // A sign-in code is redeemed only for a sign-in this app started (the `peopleorbit://sso` return
  // below). Never from an ordinary link: anyone can send a link carrying a code for their own account.
  if (path === '/session-handoff' || path.startsWith('/session-handoff/')) return null;
  return `${path}${parsed.search}${parsed.hash}`;
}

/** The same link can arrive twice on a cold start (launch URL and the retained event). */
const DUPLICATE_WINDOW_MS = 2000;

/** The URL the app was started with is handled once per process, not once per mount. */
let launchUrlHandled = false;

/** Sign-in codes already followed in this process, so a code never navigates twice (never logged). */
const followedSignInCodes = new Set<string>();

/** Tests only: forget that the launch URL was handled, so each test starts as a fresh app start. */
export function resetLaunchUrlHandledForTests(): void {
  launchUrlHandled = false;
  followedSignInCodes.clear();
}

/** The in-app route that redeems a sign-in code. */
function sessionHandoffRoute({ code, next }: { code: string; next: string }): string {
  return `/session-handoff?${new URLSearchParams({ code, next }).toString()}`;
}

/**
 * The in-app route for the OTHER code this app's browser tab can hand back: not a session, a
 * sign-up still waiting on its company details and mobile number. Always `/signup` — `next` is
 * where a finished SIGN-IN would have landed, which means nothing here. Signup.tsx redeems it.
 */
function signupHandoffRoute({ code }: { code: string }): string {
  return `/signup?${new URLSearchParams({ handoff: code }).toString()}`;
}

/**
 * Routes opened links through React Router. Mount it once, inside the router (see main.tsx).
 * Handles a link that started the app (cold start) as well as links that arrive while it runs.
 */
export function useNativeLinkRouting(): void {
  const navigate = useNavigate();
  // `navigate` changes identity with the location in a plain BrowserRouter; the subscription must not.
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    let removeListener: (() => Promise<void>) | undefined;
    let lastUrl = '';
    let lastAt = 0;

    const open = (url: string | undefined) => {
      if (!url) return;
      const now = Date.now();
      if (url === lastUrl && now - lastAt < DUPLICATE_WINDOW_MS) return;
      const signIn = parseAppReturnUrl(url);
      if (signIn) {
        // Only a sign-in THIS app started has a verifier waiting. Without one the link is not ours to
        // follow: anyone can send `peopleorbit://sso?code=` with a code for their own account, and
        // following it would sign the person in as them.
        if (!peekAppVerifier()) return;
        if (followedSignInCodes.has(signIn.code)) return;
        followedSignInCodes.add(signIn.code);
        lastUrl = url;
        lastAt = now;
        // Close the Custom Tab / SFSafariViewController (a no-op on Android, where the intent has
        // already brought the app forward). Never let a plugin error stop the sign-in itself.
        void Promise.resolve()
          .then(() => Browser.close())
          .catch(() => {});
        navigateRef.current(
          signIn.kind === 'signup' ? signupHandoffRoute(signIn) : sessionHandoffRoute(signIn),
        );
        return;
      }
      const to = routeForOpenedUrl(url);
      if (!to) return;
      lastUrl = url;
      lastAt = now;
      navigateRef.current(to);
    };

    // A contact picked while the phone killed the app: back to sign-up's team screen, which reopens
    // from the saved draft and adds it (contactPicker.ts, signupDraft.ts). Already on sign-up, the
    // page reopens the right screen by itself.
    listenForRestoredContact(() => {
      if (!window.location.pathname.startsWith('/signup')) navigateRef.current('/signup/team');
    });

    App.addListener('appUrlOpen', (event) => open(event.url))
      .then((handle) => {
        if (cancelled) void handle.remove();
        else removeListener = () => handle.remove();
      })
      .catch(() => {
        // No App plugin (an older shell): links just open the app's start page.
      });

    if (!launchUrlHandled) {
      launchUrlHandled = true;
      App.getLaunchUrl()
        // Not guarded by `cancelled`: the flag above is already spent, and `navigateRef` stays valid.
        .then((launch) => open(launch?.url))
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      void removeListener?.();
    };
  }, []);
}

/** Renders nothing; exists so main.tsx can mount the hook inside `<BrowserRouter>`. */
export function NativeLinkRouting(): null {
  useNativeLinkRouting();
  return null;
}
